"use client"

import Link from "next/link"
import {
  ArrowRight,
  Download,
  Home,
  Pause,
  Play,
  RotateCcw,
  Share2,
  SlidersHorizontal,
  Vibrate,
  VibrateOff,
  Volume2,
  VolumeX,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useModalFocusManagement } from "@/hooks/use-modal-focus-management"
import { trackEvent, type EventProperties } from "@/lib/analytics"
import {
  M_GRASSES,
  type MowGrassId,
  type MowSeasonId,
  type MowerTypeId,
} from "@/lib/mowing-plan"
import {
  createLawnStripeRunId,
  type LawnStripePattern,
  type LawnStripePlayMode,
  type LawnStripeRunSummary,
} from "@/lib/lawn-stripe-studio"
/*
 * Phaser remains behind the dynamic import below. This module only imports the
 * pure deterministic engine contracts used by React and analytics.
 */
import type {
  LawnStripeGameHandle,
  LawnStripePhase,
  LawnStripeSceneStats,
} from "./game/types"
import {
  LAWN_STRIPE_HAPTICS_STORAGE_KEY,
  LAWN_STRIPE_SOUND_STORAGE_KEY,
} from "./game/constants"

const BEST_SCORE_KEY = "wgis:lawn-stripe-studio:best-score"

const INITIAL_STATS: LawnStripeSceneStats = {
  coveragePercent: 0,
  distanceCells: 0,
  elapsedSeconds: 0,
  bestScore: 0,
  remainingSeconds: 90,
}

const PATTERNS: Array<{
  id: LawnStripePattern
  name: string
  hint: string
}> = [
  { id: "straight", name: "Straight", hint: "Clean parallel passes" },
  { id: "diagonal", name: "Diagonal", hint: "Aim corner to corner" },
  { id: "checkerboard", name: "Checkerboard", hint: "Cross the first cut" },
]

function createRunId() {
  let entropy: string
  try {
    entropy = crypto.randomUUID()
  } catch {
    entropy = Math.random().toString(36).slice(2, 12)
  }
  return createLawnStripeRunId({ startedAtMs: Date.now(), entropy })
}

function trackStripeEvent(name: string, properties: EventProperties) {
  const detail = { name, properties: { schema_version: 1, ...properties } }
  trackEvent(name, detail.properties)
  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("wgis:lawn-stripe-analytics", { detail }))
  }
}

function getDeviceType() {
  if (typeof window === "undefined") return "desktop"
  return window.matchMedia("(pointer: coarse)").matches ? "mobile" : "desktop"
}

function currentSeason(): MowSeasonId {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return "spring"
  if (month >= 5 && month <= 7) return "summer"
  if (month >= 8 && month <= 10) return "fall"
  return "winter"
}

function readSoundEnabled() {
  if (typeof window === "undefined") return true
  try {
    return window.localStorage.getItem(LAWN_STRIPE_SOUND_STORAGE_KEY) !== "0"
  } catch {
    return true
  }
}

function readHapticsEnabled() {
  if (typeof window === "undefined") return true
  try {
    return window.localStorage.getItem(LAWN_STRIPE_HAPTICS_STORAGE_KEY) !== "0"
  } catch {
    return true
  }
}

function readBestScore() {
  if (typeof window === "undefined") return 0
  try {
    const parsed = Number.parseInt(window.localStorage.getItem(BEST_SCORE_KEY) ?? "0", 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  } catch {
    return 0
  }
}

function writeBestScore(score: number) {
  const next = Math.max(readBestScore(), Math.round(score))
  try {
    window.localStorage.setItem(BEST_SCORE_KEY, String(next))
  } catch {
    // A private browser may disable storage. The current result still renders.
  }
  return next
}

function getDebugComplete() {
  if (typeof window === "undefined" || process.env.NODE_ENV === "production") return false
  return new URLSearchParams(window.location.search).get("debugGame") === "complete"
}

function resolveGrass(value: string | null) {
  const normalized = (value ?? "Tall Fescue").trim().toLowerCase().replace(/[^a-z0-9]/g, "")
  const match = (Object.entries(M_GRASSES) as Array<[MowGrassId, (typeof M_GRASSES)[MowGrassId]]>)
    .find(([id, grass]) => [id, grass.name, grass.engineKey]
      .some((candidate) => candidate.toLowerCase().replace(/[^a-z0-9]/g, "") === normalized))
  const [id, grass] = match ?? (["fescue", M_GRASSES.fescue] as const)
  return { id, name: grass.engineKey }
}

function resolveSeason(value: string | null): MowSeasonId {
  return value && ["spring", "summer", "fall", "winter"].includes(value)
    ? value as MowSeasonId
    : currentSeason()
}

function resolveMower(value: string | null): MowerTypeId {
  return value === "reel" || value === "robotic" ? value : "rotary"
}

function dataUrlToFile(dataUrl: string, filename: string) {
  const [header, body] = dataUrl.split(",")
  if (!header || !body) return null
  const mime = header.match(/data:([^;]+)/)?.[1] ?? "image/png"
  const bytes = Uint8Array.from(atob(body), (character) => character.charCodeAt(0))
  return new File([bytes], filename, { type: mime })
}

export function LawnStripeStudioGame() {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const pausedDialogRef = useRef<HTMLDivElement | null>(null)
  const resumeRef = useRef<HTMLButtonElement | null>(null)
  const resultRef = useRef<HTMLDivElement | null>(null)
  const resultPrimaryRef = useRef<HTMLAnchorElement | null>(null)
  const gameRef = useRef<LawnStripeGameHandle | null>(null)
  const viewedRef = useRef(false)
  const contextRef = useRef({ grassType: "Tall Fescue", grassId: "fescue" as MowGrassId, season: currentSeason(), entrySource: "direct", mowerType: "rotary" as MowerTypeId })
  const [phase, setPhase] = useState<LawnStripePhase>("loading")
  const [mode, setMode] = useState<LawnStripePlayMode>("zen")
  const [pattern, setPattern] = useState<LawnStripePattern>("straight")
  const [runId, setRunId] = useState(() => createRunId())
  const viewedRunIdRef = useRef(runId)
  const [stats, setStats] = useState<LawnStripeSceneStats>(INITIAL_STATS)
  const [completion, setCompletion] = useState<LawnStripeRunSummary | null>(null)
  const [beforeImage, setBeforeImage] = useState<string | null>(null)
  const [afterImage, setAfterImage] = useState<string | null>(null)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [hapticsEnabled, setHapticsEnabled] = useState(true)
  const [shareStatus, setShareStatus] = useState("")
  const [grassType, setGrassType] = useState("Tall Fescue")
  const [grassId, setGrassId] = useState<MowGrassId>("fescue")
  const [season, setSeason] = useState<MowSeasonId>(currentSeason)
  const [mowerType, setMowerType] = useState<MowerTypeId>("rotary")
  const [entrySource, setEntrySource] = useState("direct")
  const debugComplete = useMemo(() => getDebugComplete(), [])

  useEffect(() => {
    setSoundEnabled(readSoundEnabled())
    setHapticsEnabled(readHapticsEnabled())
    const params = new URLSearchParams(window.location.search)
    const nextGrass = resolveGrass(params.get("grass"))
    const nextSeason = resolveSeason(params.get("season"))
    const nextMower = resolveMower(params.get("mower"))
    const nextSource = params.get("source") || "direct"
    const nextMode = params.get("mode")
    const nextPattern = params.get("pattern")
    setGrassType(nextGrass.name)
    setGrassId(nextGrass.id)
    setSeason(nextSeason)
    setMowerType(nextMower)
    setEntrySource(nextSource)
    if (nextMode === "zen" || nextMode === "challenge") setMode(nextMode)
    if (nextPattern === "straight" || nextPattern === "diagonal" || nextPattern === "checkerboard") {
      setPattern(nextPattern)
    }
    contextRef.current = { grassType: nextGrass.name, grassId: nextGrass.id, season: nextSeason, entrySource: nextSource, mowerType: nextMower }

    if (!viewedRef.current) {
      viewedRef.current = true
      trackStripeEvent("lawn_stripe_game_viewed", {
        run_id: viewedRunIdRef.current,
        device_type: getDeviceType(),
        grass_type: nextGrass.name,
        mower_type: nextMower,
        source: nextSource,
      })
    }
  }, [])

  useEffect(() => {
    contextRef.current = { grassType, grassId, season, entrySource, mowerType }
  }, [entrySource, grassId, grassType, mowerType, season])

  const resumeGame = useCallback(() => {
    gameRef.current?.resume()
  }, [])

  useModalFocusManagement({
    isOpen: phase === "paused",
    containerRef: pausedDialogRef,
    initialFocusRef: resumeRef,
    onEscape: resumeGame,
  })

  useModalFocusManagement({
    isOpen: phase === "complete" && completion !== null,
    containerRef: resultRef,
    initialFocusRef: resultPrimaryRef,
  })

  useEffect(() => {
    const parent = parentRef.current
    if (!parent || gameRef.current) return

    let cancelled = false
    let handle: LawnStripeGameHandle | null = null
    const bestScore = readBestScore()
    setStats((current) => ({ ...current, bestScore }))

    async function mountGame() {
      const { createLawnStripeGame } = await import("./game/create-game")
      if (cancelled || !parentRef.current) return

      handle = createLawnStripeGame({
        parent: parentRef.current,
        bestScore,
        debugComplete,
        mode,
        pattern,
        runId,
        grass: grassId,
        season,
        mower: mowerType,
        callbacks: {
          onReady: () => {
            setPhase("title")
            window.setTimeout(() => {
              handle?.captureImage().then(setBeforeImage)
            }, 80)
          },
          onPhaseChange: setPhase,
          onStatsChange: setStats,
          onComplete: (summary) => {
            const nextBest = writeBestScore(summary.overallScore)
            setCompletion(summary)
            setStats((current) => ({ ...current, bestScore: nextBest }))
            setPhase("complete")
            const context = contextRef.current
            trackStripeEvent("lawn_stripe_game_completed", {
              run_id: summary.runId,
              mode: summary.mode,
              pattern: summary.pattern,
              device_type: getDeviceType(),
              grass_type: context.grassType,
              mower_type: context.mowerType,
              duration_seconds: summary.durationSeconds,
              coverage_percent: summary.coveragePercent,
              straightness_score: summary.straightnessScore,
              efficiency_score: summary.efficiencyScore,
              turf_health_score: summary.turfHealthScore,
              overall_score: summary.overallScore,
              rank: summary.rank,
            })
            window.setTimeout(() => {
              handle?.captureImage().then(setAfterImage)
            }, 120)
          },
        },
      })
      gameRef.current = handle
    }

    mountGame()

    return () => {
      cancelled = true
      handle?.destroy()
      if (gameRef.current === handle) gameRef.current = null
      parent.replaceChildren()
    }
    // The scene receives current selections in start/restart payloads. It only mounts once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debugComplete])

  const startRun = useCallback(() => {
    let nextRunId = runId
    if (completion) {
      nextRunId = createRunId()
      trackStripeEvent("lawn_stripe_game_restarted", {
        run_id: completion.runId,
        next_run_id: nextRunId,
        mode,
        pattern,
        device_type: getDeviceType(),
        grass_type: grassType,
        mower_type: mowerType,
        overall_score: completion.overallScore,
        restart_source: "pattern_setup",
      })
      setRunId(nextRunId)
    }
    setCompletion(null)
    setAfterImage(null)
    setShareStatus("")
    trackStripeEvent("lawn_stripe_game_started", {
      run_id: nextRunId,
      mode,
      pattern,
      device_type: getDeviceType(),
      grass_type: grassType,
      mower_type: mowerType,
      best_score: stats.bestScore,
      source: entrySource,
    })
    gameRef.current?.start({ mode, pattern, runId: nextRunId, grass: grassId, season, mower: mowerType })
    parentRef.current?.focus()
  }, [completion, entrySource, grassId, grassType, mode, mowerType, pattern, runId, season, stats.bestScore])

  const restartRun = useCallback(() => {
    const nextRunId = createRunId()
    trackStripeEvent("lawn_stripe_game_restarted", {
      run_id: completion?.runId ?? runId,
      next_run_id: nextRunId,
      mode,
      pattern,
      device_type: getDeviceType(),
      grass_type: grassType,
      mower_type: mowerType,
      overall_score: completion?.overallScore ?? 0,
      restart_source: completion ? "result" : "pause",
    })
    trackStripeEvent("lawn_stripe_game_started", {
      run_id: nextRunId,
      mode,
      pattern,
      device_type: getDeviceType(),
      grass_type: grassType,
      mower_type: mowerType,
      best_score: stats.bestScore,
      source: entrySource,
    })
    setRunId(nextRunId)
    setCompletion(null)
    setAfterImage(null)
    setShareStatus("")
    gameRef.current?.restart({ mode, pattern, runId: nextRunId, grass: grassId, season, mower: mowerType })
    parentRef.current?.focus()
  }, [completion, entrySource, grassId, grassType, mode, mowerType, pattern, runId, season, stats.bestScore])

  const choosePattern = useCallback((nextPattern: LawnStripePattern) => {
    setPattern(nextPattern)
    trackStripeEvent("lawn_stripe_pattern_selected", {
      run_id: runId,
      pattern: nextPattern,
      mode,
      device_type: getDeviceType(),
      grass_type: grassType,
      mower_type: mowerType,
    })
  }, [grassType, mode, mowerType, runId])

  const steerFromClient = useCallback((clientX: number, clientY: number) => {
    if (phase !== "playing") return
    const canvas = parentRef.current?.querySelector("canvas")
    if (!canvas) return
    const bounds = canvas.getBoundingClientRect()
    if (bounds.width <= 0 || bounds.height <= 0) return
    const xRatio = Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width))
    const yRatio = Math.max(0, Math.min(1, (clientY - bounds.top) / bounds.height))
    gameRef.current?.steer(xRatio, yRatio)
  }, [phase])

  const toggleSound = useCallback(() => {
    setSoundEnabled((current) => {
      const next = !current
      try {
        window.localStorage.setItem(LAWN_STRIPE_SOUND_STORAGE_KEY, next ? "1" : "0")
      } catch {
        // The on-screen state remains useful when persistence is unavailable.
      }
      return next
    })
  }, [])

  const toggleHaptics = useCallback(() => {
    setHapticsEnabled((current) => {
      const next = !current
      try {
        window.localStorage.setItem(LAWN_STRIPE_HAPTICS_STORAGE_KEY, next ? "1" : "0")
      } catch {
        // The on-screen state remains useful when persistence is unavailable.
      }
      return next
    })
  }, [])

  const downloadResult = useCallback(() => {
    if (!afterImage || !completion) return
    const anchor = document.createElement("a")
    anchor.href = afterImage
    anchor.download = `lawn-stripe-${completion.pattern}.png`
    anchor.click()
    setShareStatus("Finished lawn downloaded")
    trackStripeEvent("lawn_stripe_result_shared", {
      run_id: completion.runId,
      mode: completion.mode,
      pattern: completion.pattern,
      method: "download",
      device_type: getDeviceType(),
      grass_type: grassType,
      mower_type: mowerType,
      overall_score: completion.overallScore,
    })
  }, [afterImage, completion, grassType, mowerType])

  const shareResult = useCallback(async () => {
    if (!completion) return
    const shareData: ShareData = {
      title: "My Lawn Stripe Studio finish",
      text: `I scored ${completion.overallScore} on a ${completion.pattern} lawn.`,
      url: window.location.href,
    }
    const file = afterImage
      ? dataUrlToFile(afterImage, `lawn-stripe-${completion.pattern}.png`)
      : null
    if (file && navigator.canShare?.({ files: [file] })) {
      shareData.files = [file]
    }

    try {
      if (!navigator.share) {
        downloadResult()
        return
      }
      await navigator.share(shareData)
      setShareStatus("Finished lawn shared")
      trackStripeEvent("lawn_stripe_result_shared", {
        run_id: completion.runId,
        mode: completion.mode,
        pattern: completion.pattern,
        method: shareData.files ? "native_image" : "native_link",
        device_type: getDeviceType(),
        grass_type: grassType,
        mower_type: mowerType,
        overall_score: completion.overallScore,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return
      downloadResult()
    }
  }, [afterImage, completion, downloadResult, grassType, mowerType])

  const canPause = phase === "playing" || phase === "paused"
  const compactPlaySurface = phase === "playing" || phase === "paused"
  const mowingHref = `/tools/mowing-calculator?grass=${encodeURIComponent(grassType)}&season=${encodeURIComponent(season)}&mower=${encodeURIComponent(mowerType)}&from=lawn-stripe-studio`

  return (
    <section className="min-h-[100svh] overflow-hidden bg-[#e8efcf] text-[#173126]">
      <div className={`mx-auto flex min-h-[100svh] w-full max-w-[1480px] flex-col gap-3 px-3 py-3 sm:px-5 sm:py-4 ${compactPlaySurface ? "justify-center" : ""}`}>
        <header className="flex min-h-12 items-center justify-between gap-3">
          <Link
            href="/"
            className="inline-flex size-11 items-center justify-center rounded-full border border-[#76905d] bg-[#f7f3d8] text-[#173126] shadow-sm transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
            aria-label="Back to What Grass Is This home"
          >
            <Home className="size-5" aria-hidden="true" />
          </Link>

          <div
            className="grid flex-1 grid-cols-2 items-center gap-1 text-center font-mono text-[0.66rem] font-bold uppercase tracking-[0.07em] text-[#38533a] sm:flex sm:justify-center sm:gap-7 sm:text-xs sm:tracking-[0.09em]"
          >
            <span data-testid="lawn-stripe-coverage">Cut {Math.round(stats.coveragePercent)}%</span>
            <span data-testid="lawn-stripe-run-clock">
              <span className="sm:hidden">
                {mode === "challenge" && phase === "playing"
                  ? `${stats.remainingSeconds ?? 90}s`
                  : `Best ${stats.bestScore}`}
              </span>
              <span className="hidden sm:inline">
                {mode === "challenge" && phase === "playing"
                  ? `${stats.remainingSeconds ?? 90}s`
                  : PATTERNS.find((item) => item.id === pattern)?.name}
              </span>
            </span>
            <span className="hidden sm:inline" data-testid="lawn-stripe-best">Best {stats.bestScore}</span>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={toggleSound}
              className="inline-flex size-11 items-center justify-center rounded-full border border-[#76905d] bg-[#f7f3d8] text-[#173126] shadow-sm transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
              aria-label={soundEnabled ? "Mute sound" : "Unmute sound"}
              aria-pressed={!soundEnabled}
              data-testid="lawn-stripe-sound-toggle"
            >
              {soundEnabled ? <Volume2 className="size-5" aria-hidden="true" /> : <VolumeX className="size-5" aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={toggleHaptics}
              className="inline-flex size-11 items-center justify-center rounded-full border border-[#76905d] bg-[#f7f3d8] text-[#173126] shadow-sm transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
              aria-label="Haptic feedback"
              aria-pressed={hapticsEnabled}
              title={hapticsEnabled ? "Haptic feedback on" : "Haptic feedback off"}
              data-testid="lawn-stripe-haptics-toggle"
            >
              {hapticsEnabled ? <Vibrate className="size-5" aria-hidden="true" /> : <VibrateOff className="size-5" aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={() => {
                if (phase === "paused") resumeGame()
                else gameRef.current?.pause()
              }}
              disabled={!canPause}
              className="inline-flex size-11 items-center justify-center rounded-full border border-[#76905d] bg-[#f7f3d8] text-[#173126] shadow-sm transition hover:-translate-y-0.5 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={phase === "paused" ? "Resume game" : "Pause game"}
            >
              {phase === "paused" ? <Play className="size-5" aria-hidden="true" /> : <Pause className="size-5" aria-hidden="true" />}
            </button>
          </div>
        </header>

        <div
          data-testid="lawn-stripe-game-shell"
          className={`relative overflow-hidden rounded-[1.75rem] border border-[#76905d] bg-[#dce8bd] shadow-[0_24px_80px_rgba(54,81,58,0.22)] touch-none ${
            compactPlaySurface
              ? "aspect-[3/2] min-h-0 flex-none sm:aspect-auto sm:min-h-[620px] sm:flex-1"
              : "min-h-[560px] flex-1 sm:min-h-[620px]"
          }`}
        >
          <div
            ref={parentRef}
            data-testid="lawn-stripe-canvas-parent"
            className="absolute inset-0 touch-none focus-visible:outline-3 focus-visible:outline-offset-[-4px] focus-visible:outline-[#f2c94c] [&_canvas]:touch-none"
            role="region"
            aria-label="Interactive lawn. Use WASD, arrow keys, or drag to steer the mower."
            tabIndex={0}
            onPointerDown={(event) => {
              event.preventDefault()
              steerFromClient(event.clientX, event.clientY)
            }}
            onPointerMove={(event) => {
              if (event.buttons === 0 && event.pointerType !== "touch") return
              event.preventDefault()
              steerFromClient(event.clientX, event.clientY)
            }}
            onClick={(event) => steerFromClient(event.clientX, event.clientY)}
          />

          <span
            data-testid="lawn-stripe-debug-state"
            data-player-x={stats.playerX ?? 0}
            data-player-y={stats.playerY ?? 0}
            data-reduced-motion={stats.reducedMotion ? "true" : "false"}
            aria-hidden="true"
            className="sr-only"
          />

          {(phase === "loading" || phase === "title") && (
            <div className="absolute inset-0 z-10 flex items-center justify-center overflow-y-auto bg-[#e8efcf]/82 px-4 py-5 backdrop-blur-[3px] sm:px-8">
              <div className="w-full max-w-3xl text-center">
                <p className="font-mono text-[0.7rem] font-bold uppercase tracking-[0.22em] text-[#55723f]">
                  Lawn Stripe Studio · a 90-second mowing toy
                </p>
                <h1 className="mx-auto mt-3 max-w-2xl text-4xl font-black leading-[0.94] tracking-[-0.055em] text-[#173126] sm:text-6xl">
                  Make the cleanest cut on the block.
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-[#3f5738] sm:text-base">
                  Guide the yellow mower, overlap each pass, and let the lawn become the pattern.
                </p>

                <div className="mx-auto mt-6 inline-flex rounded-full border border-[#8aa16c] bg-[#f7f3d8] p-1" role="group" aria-label="Play mode">
                  {(["zen", "challenge"] as LawnStripePlayMode[]).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setMode(item)}
                      aria-pressed={mode === item}
                      className={`min-h-10 rounded-full px-4 text-sm font-extrabold capitalize transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126] ${
                        mode === item ? "bg-[#173126] text-[#f7f3d8]" : "text-[#496044] hover:bg-white"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs font-semibold text-[#55723f]">
                  {mode === "zen"
                    ? "Zen keeps the clock out of your way."
                    : "Challenge gives you a 90-second par, then trims efficiency points."}
                </p>

                <div className="mx-auto mt-5 grid max-w-2xl grid-cols-3 gap-2 sm:gap-3" role="group" aria-label="Mowing pattern">
                  {PATTERNS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => choosePattern(item.id)}
                      aria-pressed={pattern === item.id}
                      className={`group rounded-2xl border p-2.5 text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126] sm:p-3 ${
                        pattern === item.id
                          ? "border-[#173126] bg-[#f7f3d8] shadow-[0_7px_0_#8aa16c]"
                          : "border-[#8aa16c] bg-[#eef4d5]/80 hover:-translate-y-0.5 hover:bg-[#f7f3d8]"
                      }`}
                    >
                      <PatternSwatch pattern={item.id} />
                      <span className="mt-2 block text-xs font-black sm:text-sm">{item.name}</span>
                      <span className="mt-0.5 hidden text-[0.68rem] leading-4 text-[#55723f] sm:block">{item.hint}</span>
                    </button>
                  ))}
                </div>

                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={startRun}
                    disabled={phase === "loading"}
                    className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#f2c94c] px-6 py-3 text-sm font-black text-[#173126] shadow-[0_8px_0_#b78922] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_10px_0_#b78922] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#173126] active:translate-y-1 active:shadow-[0_3px_0_#b78922] disabled:cursor-wait disabled:opacity-60"
                  >
                    <Play className="size-5" aria-hidden="true" />
                    Start mowing
                  </button>
                  <span className="font-mono text-[0.68rem] font-bold uppercase tracking-[0.1em] text-[#55723f]">
                    WASD, arrows, or drag
                  </span>
                </div>
              </div>
            </div>
          )}

          {phase === "paused" && (
            <div
              ref={pausedDialogRef}
              className="absolute inset-0 z-20 flex items-center justify-center bg-[#173126]/48 px-5 text-center backdrop-blur-[3px] focus:outline-none"
              role="dialog"
              aria-modal="true"
              aria-labelledby="lawn-stripe-paused-title"
              tabIndex={-1}
            >
              <div className="rounded-[1.5rem] border border-[#a9bd86] bg-[#f7f3d8] p-6 shadow-2xl">
                <p className="font-mono text-[0.68rem] font-bold uppercase tracking-[0.2em] text-[#55723f]">Mower parked</p>
                <h2 id="lawn-stripe-paused-title" className="mt-2 text-3xl font-black tracking-[-0.04em]">Take a breather.</h2>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button
                    ref={resumeRef}
                    type="button"
                    onClick={resumeGame}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#f2c94c] px-5 py-2 text-sm font-black shadow-[0_6px_0_#b78922] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
                  >
                    <Play className="size-4" aria-hidden="true" />
                    Resume
                  </button>
                  <button
                    type="button"
                    onClick={restartRun}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#76905d] bg-white px-5 py-2 text-sm font-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                    Restart
                  </button>
                </div>
              </div>
            </div>
          )}

          {phase === "complete" && completion && (
            <div
              ref={resultRef}
              data-testid="lawn-stripe-result"
              className="absolute inset-0 z-20 overflow-y-auto bg-[#173126]/54 px-3 py-4 backdrop-blur-[4px] focus:outline-none sm:px-6 sm:py-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="lawn-stripe-result-title"
              tabIndex={-1}
            >
              <div className="mx-auto max-w-4xl rounded-[1.6rem] border border-[#a9bd86] bg-[#f7f3d8] p-3 shadow-2xl sm:p-6">
                <div className="flex flex-col gap-3 sm:gap-5 lg:flex-row lg:items-start">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[0.68rem] font-bold uppercase tracking-[0.2em] text-[#55723f]">Lawn complete</p>
                    <h2 id="lawn-stripe-result-title" className="mt-2 text-4xl font-black leading-none tracking-[-0.05em] sm:text-5xl">{completion.rank}</h2>
                    <p className="mt-1 text-sm leading-5 text-[#496044] sm:mt-2 sm:leading-6">
                      {mode === "zen" ? "No timer pressure. Just a finished lawn and the route you made." : "Every straighter pass left more score on the lawn."}
                    </p>

                    <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:grid-cols-4" data-testid="lawn-stripe-score-grid">
                      <ScoreCell label="Coverage" value={`${Math.round(completion.coveragePercent)}%`} />
                      <ScoreCell label="Straightness" value={String(completion.straightnessScore)} />
                      <ScoreCell label="Efficiency" value={String(completion.efficiencyScore)} />
                      <ScoreCell label="Turf health" value={String(completion.turfHealthScore)} />
                    </div>

                    <div className="mt-3 flex items-baseline gap-2 sm:mt-4">
                      <span className="font-mono text-[0.68rem] font-bold uppercase tracking-[0.14em] text-[#55723f]">Finish score</span>
                      <strong className="text-3xl font-black tracking-[-0.04em]" data-testid="lawn-stripe-overall-score">{completion.overallScore}</strong>
                    </div>
                  </div>

                  <div className="grid min-w-0 flex-1 grid-cols-2 gap-2" aria-label="Before and after lawn">
                    <ResultImage label="Before" src={beforeImage} fallback="overgrown" />
                    <ResultImage label="After" src={afterImage} fallback="striped" />
                  </div>
                </div>

                <p className="sr-only" aria-live="polite">{shareStatus}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 sm:mt-5">
                  <Link
                    ref={resultPrimaryRef}
                    href={mowingHref}
                    onClick={() => {
                      trackStripeEvent("lawn_stripe_game_cta_clicked", {
                        run_id: completion.runId,
                        mode: completion.mode,
                        pattern: completion.pattern,
                        device_type: getDeviceType(),
                        grass_type: grassType,
                        mower_type: mowerType,
                        overall_score: completion.overallScore,
                        cta_target: "mowing_calculator",
                      })
                    }}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#173126] px-5 py-2 text-sm font-black text-[#f7f3d8] transition hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
                  >
                    Get my real mow height
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                  <button
                    type="button"
                    onClick={restartRun}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#76905d] bg-white px-4 py-2 text-sm font-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                    Mow again
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhase("title")}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#76905d] bg-white px-4 py-2 text-sm font-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
                  >
                    <SlidersHorizontal className="size-4" aria-hidden="true" />
                    Change pattern
                  </button>
                  <button
                    type="button"
                    onClick={downloadResult}
                    disabled={!afterImage}
                    className="inline-flex size-11 items-center justify-center rounded-full border border-[#76905d] bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126] disabled:opacity-40"
                    aria-label="Download finished lawn image"
                  >
                    <Download className="size-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={shareResult}
                    className="inline-flex size-11 items-center justify-center rounded-full border border-[#76905d] bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#173126]"
                    aria-label="Share finished lawn"
                  >
                    <Share2 className="size-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="flex min-h-6 items-center justify-between gap-3 px-1 font-mono text-[0.66rem] font-bold uppercase tracking-[0.08em] text-[#55723f]">
          <span data-testid="lawn-stripe-phase">{phase}</span>
          <span>{phase === "playing" ? "Aim ahead, overlap the edge" : `${grassType} · ${season}`}</span>
        </footer>
        <p className="sr-only" aria-live="polite">
          {phase === "paused" ? "Mowing paused." : phase === "complete" ? "Lawn complete. Results ready." : ""}
        </p>
      </div>
    </section>
  )
}

function PatternSwatch({ pattern }: { pattern: LawnStripePattern }) {
  const background =
    pattern === "straight"
      ? "repeating-linear-gradient(90deg, #6f9c49 0 12px, #a7c966 12px 24px)"
      : pattern === "diagonal"
        ? "repeating-linear-gradient(135deg, #6f9c49 0 12px, #a7c966 12px 24px)"
        : "repeating-conic-gradient(from 45deg, #6f9c49 0 25%, #a7c966 0 50%) 0 0 / 24px 24px"

  return <span className="block h-10 rounded-xl border border-[#6e8d54] shadow-inner sm:h-12" style={{ background }} aria-hidden="true" />
}

function ScoreCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[#b6c79a] bg-[#eef4d5] px-3 py-2">
      <span className="block font-mono text-[0.6rem] font-bold uppercase tracking-[0.1em] text-[#55723f]">{label}</span>
      <strong className="mt-0.5 block text-lg font-black">{value}</strong>
    </div>
  )
}

function ResultImage({
  label,
  src,
  fallback,
}: {
  label: string
  src: string | null
  fallback: "overgrown" | "striped"
}) {
  return (
    <figure className="overflow-hidden rounded-xl border border-[#9caf7f] bg-[#dbe7bd]">
      <div className="aspect-[4/3]">
        {src ? (
          // The image is a same-origin Phaser snapshot generated in this browser.
          <img src={src} alt={`${label} virtual lawn`} className="size-full object-cover" />
        ) : (
          <div
            className="size-full"
            aria-hidden="true"
            style={{
              background:
                fallback === "overgrown"
                  ? "#477e3d"
                  : "repeating-linear-gradient(135deg, #628f43 0 18px, #9abd5a 18px 36px)",
            }}
          />
        )}
      </div>
      <figcaption className="border-t border-[#9caf7f] bg-[#f7f3d8] px-2 py-1.5 text-center font-mono text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[#55723f]">
        {label}
      </figcaption>
    </figure>
  )
}
